import { Link } from "react-router-dom";
import jacaiLogo from "../images/JACAiLandingLogo.png";
import Seo from "../components/Seo";
import "../css/ChatSelection.css";

const businessPageTitle = "Demo chatbot AI pentru afaceri locale | JACAi Business";

const businessPageDescription =
  "JACAi Business este un proiect personal și un demo experimental de chatbot AI pentru afaceri locale, cu răspunsuri în română despre program, tarife, servicii și reguli. Momentan nu este disponibil comercial.";

const businessKeywords = [
  "chatbot AI România",
  "chatbot pentru afaceri",
  "asistent virtual site",
  "automatizare întrebări clienți",
  "chatbot fără cod",
  "JACAi Business",
  "Business Chat JACAi",
  "demo chatbot AI",
  "chatbot pentru afaceri locale",
  "chatbot răspunsuri în română",
  "chatbot program prețuri servicii",
  "automatizare întrebări repetitive",
];

const landingStructuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "@id": "https://chatjacai.com/#organization",
      name: "JACAi",
      alternateName: "Just A Chill AI",
      url: "https://chatjacai.com/",
      logo: "https://chatjacai.com/jacai-selection-logo.png",
    },
    {
      "@type": "WebSite",
      "@id": "https://chatjacai.com/#website",
      name: "JACAi - Just A Chill AI",
      url: "https://chatjacai.com/",
      publisher: { "@id": "https://chatjacai.com/#organization" },
      inLanguage: ["ro", "en"],
      description: "Platformă AI cu Business Chat JACAi și Educational Chat JACAi.",
    },
    {
      "@type": "ItemList",
      name: "Servicii JACAi",
      numberOfItems: 2,
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Business Chat JACAi", url: "https://business.chatjacai.com/" },
        { "@type": "ListItem", position: 2, name: "Educational Chat JACAi", url: "https://chatjacai.com/educational-chat" },
      ],
    },
    {
      "@type": ["SoftwareApplication", "WebApplication"],
      "@id": "https://business.chatjacai.com/#software",
      name: "JACAi Business",
      alternateName: businessPageTitle,
      applicationCategory: "BusinessApplication",
      applicationSubCategory: "AI chatbot",
      operatingSystem: "Web",
      url: "https://business.chatjacai.com/",
      image: "https://chatjacai.com/og-image.png",
      description: businessPageDescription,
      inLanguage: "ro-RO",
      areaServed: {
        "@type": "Country",
        name: "România",
      },
      audience: {
        "@type": "BusinessAudience",
        audienceType: "Afaceri mici și locale din România",
      },
      provider: { "@id": "https://chatjacai.com/#organization" },
      featureList: [
        "Demonstrație de răspunsuri AI pentru afaceri locale",
        "Răspunsuri în limba română despre program, tarife, servicii și reguli",
        "Istoric al conversației",
        "Întrebări sugerate despre programări, prețuri și servicii",
      ],
      isAccessibleForFree: true,
      releaseNotes:
        "Proiect personal în dezvoltare, demo experimental și indisponibil comercial în acest moment.",
      keywords: businessKeywords.join(", "),
    },
    {
      "@type": "Service",
      "@id": "https://business.chatjacai.com/#demo-service",
      name: "Business Chat JACAi — demo chatbot AI",
      serviceType: "Demonstrație de asistent virtual AI pentru afaceri locale",
      url: "https://business.chatjacai.com/",
      description: businessPageDescription,
      provider: { "@id": "https://chatjacai.com/#organization" },
      areaServed: { "@type": "Country", name: "România" },
      audience: {
        "@type": "BusinessAudience",
        audienceType:
          "Service-uri auto, saloane de înfrumusețare, firme de curățenie, săli de fitness, restaurante și servicii profesionale",
      },
      isRelatedTo: { "@id": "https://business.chatjacai.com/#software" },
    },
  ],
};

const chatOptions = [
  {
    title: "Business Chat JACAi",
    description:
      "Demo experimental de chatbot AI pentru întrebări despre program, tarife și servicii.",
    path: "https://business.chatjacai.com",
    icon: "briefcase",
    external: true,
  },
  {
    title: "Educational Chat JACAi",
    description: "Explicații clare și ajutor pentru învățare, într-un singur loc.",
    path: "/educational-chat",
    icon: "book",
  },
];

function OptionIcon({ type }) {
  if (type === "briefcase") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M9 7V5.8C9 4.8 9.8 4 10.8 4h2.4c1 0 1.8.8 1.8 1.8V7m-10 4.5h14M6 7h12a2 2 0 0 1 2 2v8.5a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2Z" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 5.5A2.5 2.5 0 0 1 6.5 3H11a2 2 0 0 1 2 2v16a2 2 0 0 0-2-2H6.5A2.5 2.5 0 0 0 4 21.5v-16Zm16 0A2.5 2.5 0 0 0 17.5 3H13v18a2 2 0 0 1 2-2h2.5a2.5 2.5 0 0 1 2.5 2.5v-16Z" />
    </svg>
  );
}

export default function ChatSelection() {
  return (
    <main className="chat-selection">
      <Seo
        title="JACAi | Business Chat AI și Educational Chat"
        description="Descoperă Business Chat JACAi, un demo experimental de chatbot AI pentru afaceri locale, și Educational Chat JACAi pentru explicații și suport în învățare."
        keywords={businessKeywords}
        category="business software"
        image="https://business.chatjacai.com/og-image-business.png"
        imageAlt="JACAi Business — demo experimental de chatbot AI pentru afaceri locale"
        structuredData={landingStructuredData}
      />
      <div className="chat-selection__glow chat-selection__glow--blue" />
      <div className="chat-selection__glow chat-selection__glow--teal" />

      <section className="chat-selection__content" aria-labelledby="chat-selection-title">
        <div className="chat-selection__brand">
          <img src={jacaiLogo} alt="JACAi" className="chat-selection__logo" />
          <span>JACAi</span>
        </div>

        <p className="chat-selection__eyebrow">Just A Chill AI</p>
        <h1 id="chat-selection-title">Alege experiența potrivită pentru tine</h1>
        <p className="chat-selection__intro">
          Două soluții AI specializate, concepute pentru obiective profesionale și educaționale.
        </p>

        <div className="chat-selection__options">
          {chatOptions.map((option) => {
            const content = (
              <>
              <span className={`chat-option__icon chat-option__icon--${option.icon}`}>
                <OptionIcon type={option.icon} />
              </span>
              <span className="chat-option__copy">
                <strong>{option.title}</strong>
                <small>{option.description}</small>
              </span>
              <span className="chat-option__arrow" aria-hidden="true">→</span>
              </>
            );

            return option.external ? (
              <a className="chat-option" href={option.path} key={option.path}>
                {content}
              </a>
            ) : (
              <Link className="chat-option" to={option.path} key={option.path}>
                {content}
              </Link>
            );
          })}
        </div>
      </section>
    </main>
  );
}
